/*
 * `theTime` and `theUTCTime` factory functions
 */

import { isAccepted, setNewDate, type Settings } from "./functions";
import { TimeDate, type Factory } from "./theTimeClass";

// What flows on from `findInit`, which has already read a given nullish as `NaN` — `Factory` is what a caller may pass
type DateInit = Date | TimeDate | Settings | string | number;

/**
 * Returns new Date object (extended TimeDate object).
 *
 * - No argument is the current time; an explicit `undefined` or `null` is an Invalid Date.
 * - If `t` is not set (excluding `add` time), local time will be used,
 *   else if `t` is set, even empty string, unset time components will default to 0.
 * - Option `MM` to set Date start from 1, not native 0, others are intuitive as original.
 *
 * @param init: string | number | Date | TimeDate | Settings | null – Date object, date time string, epoch milliseconds, set options, or `null` as absence.
 * @param whereOptions: Settings -- set options set init when init isn't options.
 * @returns TimeDate – extended Date object. */
const theTime: Factory = (...args) => {
	// Z is one-off; theUTCTime is the persistent UTC variant
	const init = findInit(args);
	const settings = findSettings(init, args[1]);

	return setNewDate(
		settings,
		newTime(theTime, init, { parseZ: settings.Z ?? false }),
		false,
	) as TimeDate;
};

/** Like `theTime`, but persistently UTC — the mode rides the object through `.where` / `.goes` chains. */
const theUTCTime: Factory = (...args) => {
	const init = findInit(args);
	const settings = findSettings(init, args[1]);

	return setNewDate(
		{ Z: true, ...settings },
		newTime(theUTCTime, init, { Z: true, parseZ: settings.Z ?? true }),
		false,
	) as TimeDate;
};

// @returns TimeDate from a date time string, `Z` reading a zoneless one as UTC
const newTimeFromDateTimeString = (dT: string, factory: Factory, Z = false): TimeDate =>
	(([, YYYY, MM = "01", DD = "01", T, zone] = []) =>
		!YYYY ? new TimeDate({ init: new Date(NaN), next: factory, Z })
			// A zone needs the native parse; `T` cannot carry one
			: zone ? new TimeDate({ init: `${YYYY}-${MM}-${DD}T${T}`, next: factory, Z })
				: factory({ YYYY, MM, DD, T: T ?? 0, Z }) as TimeDate
	// `YYYY -MM -DD THH :mm :ss .sss Z`, optional from the right, a space may replace `T`. Shape, not range.
	)(/^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:[T ](\d{2}(?::\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[-+]\d{2}:\d{2})?)?))?)?)?$/.exec(dT) ?? []);

// @returns object from init, string, or a new TimeDate object.
// `Z` is the object's mode, from the factory; `parseZ` reads a zoneless string as UTC.
const newTime = (
	factory: Factory,
	init?: DateInit,
	opts: { Z?: boolean; parseZ?: boolean } = {},
): TimeDate =>
	// A timestamp arrives as a Date, since `new Date(ms)` takes one
	((at) =>
		at instanceof Date
			? new TimeDate({ init: at, Z: opts.Z, next: factory }) : typeof at === "string"
				? newTimeFromDateTimeString(at, factory, opts.parseZ ?? opts.Z)
				// A kind that cannot be read gives an Invalid Date, not today's date
				: new TimeDate({
					...(at === undefined || isAccepted.setting(at) ? {} : { init: new Date(NaN) }),
					Z: opts.Z, next: factory,
				})
	)(typeof init === "number" ? new Date(init) : init);

// Only a missing argument is absent, as in `Date`; a nullish init that was given reads as `NaN` does
const findInit = (args: Parameters<Factory>) => (args.length === 0 ? undefined : args[0] ?? NaN);

// A kind the parameter cannot honour becomes a setting the gate rejects, so the failure travels with the object
const findSettings = (init?: DateInit, addOptions?: Settings): Settings =>
	((s) => (
		isAccepted.setting(s) ? s : { epoch: NaN }
	))(
		init instanceof Object && !(init instanceof Date)
			? init
			: addOptions === undefined ? {} : addOptions
	);

export default theTime;
export { theTime, theUTCTime, type TimeDate };
